/**
 * Payment processing utilities and constants
 * Centralizes payment-related functionality for better maintainability
 */

import { useCallback } from 'react';

/**
 * Constants for storage keys
 */
export const STORAGE_KEYS = {
  LAST_PURCHASE_RESULT: 'last_purchase_result',
  LAST_PURCHASE_BACKUP: 'last_purchase_backup',
  TRANSACTION_PREFIX: 'transaction_',
  DIRECT_NAVIGATION_FLAG: 'direct_navigation_flag'
};

/**
 * Default values for transaction data
 */
export const DEFAULT_VALUES = {
  TOKEN_AMOUNT: 100,
  PRICE_AMOUNT: 9.80,
};

/**
 * Transaction data interface
 */
export interface TransactionData {
  success: boolean;
  sessionId: string;
  paymentIntentId?: string;
  tokenAmount: number;
  price: number;
  date?: string;
  error?: string;
  payment_status?: string;
  status?: string;
}

/**
 * Store transaction data safely in session and local storage
 */
export const storeTransactionData = (data: TransactionData): void => {
  try {
    // Primary storage in sessionStorage
    sessionStorage.setItem(STORAGE_KEYS.LAST_PURCHASE_RESULT, JSON.stringify(data));
    
    // Backup in localStorage
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_PURCHASE_BACKUP, JSON.stringify(data));
    } catch (e) {
      // Silent fail for localStorage - sessionStorage is the primary store
    }
  } catch (e) {
    console.error('Failed to store transaction data', e);
  }
};

/**
 * Retrieve transaction data from storage
 */
export const retrieveTransactionData = (): TransactionData | null => {
  try {
    // Try primary storage first
    let dataStr = sessionStorage.getItem(STORAGE_KEYS.LAST_PURCHASE_RESULT);
    
    // If not found, try backup
    if (!dataStr) {
      dataStr = localStorage.getItem(STORAGE_KEYS.LAST_PURCHASE_BACKUP);
      
      // Clean up backup if used
      if (dataStr) {
        localStorage.removeItem(STORAGE_KEYS.LAST_PURCHASE_BACKUP);
      }
    }
    
    // Parse and return if found
    if (dataStr) {
      const data = JSON.parse(dataStr);
      // Clean up primary storage
      sessionStorage.removeItem(STORAGE_KEYS.LAST_PURCHASE_RESULT);
      return data;
    }
    
    return null;
  } catch (e) {
    console.error('Error retrieving transaction data', e);
    return null;
  }
};

/**
 * Create a transaction data object
 */
export const createTransactionData = (
  sessionId: string,
  tokenAmount: number,
  price: number,
  success = true,
  error?: string,
  payment_status?: string,
  status?: string,
  paymentIntentId?: string
): TransactionData => {
  return {
    success,
    sessionId,
    paymentIntentId,
    tokenAmount,
    price,
    date: new Date().toISOString(),
    ...(error ? { error } : {}),
    ...(payment_status ? { payment_status } : {}),
    ...(status ? { status } : {})
  };
};

/**
 * Check if an error is related to authentication failure
 */
export const isAuthenticationFailure = (data: TransactionData): boolean => {
  // Check payment status
  if (data.payment_status) {
    const authRelatedStatus = [
      'requires_action',
      'requires_payment_method',
      'requires_confirmation',
      'requires_capture'
    ];
    
    if (data.payment_status !== 'paid' && 
        (data.payment_status === 'unpaid' || authRelatedStatus.includes(data.payment_status))) {
      return true;
    }
  }
  
  // Check error message
  if (data.error && (
    data.error.toLowerCase().includes('authentication') ||
    data.error.toLowerCase().includes('auth') ||
    data.error.toLowerCase().includes('3ds') ||
    data.error.toLowerCase().includes('verification') ||
    data.error.toLowerCase().includes('requires_action') ||
    data.error.toLowerCase().includes('requires_payment_method')
  )) {
    return true;
  }
  
  // Check status field
  if (data.status === 'failed') {
    return true;
  }
  
  return false;
};

/**
 * Set the direct navigation flag to prevent race conditions with PaymentRedirectHandler
 */
export const setDirectNavigationFlag = (timeoutMs = 5000): void => {
  sessionStorage.setItem(STORAGE_KEYS.DIRECT_NAVIGATION_FLAG, 'true');
  
  // Auto cleanup after specified time
  setTimeout(() => {
    sessionStorage.removeItem(STORAGE_KEYS.DIRECT_NAVIGATION_FLAG);
  }, timeoutMs);
};

/**
 * Check if we have a direct navigation flag set
 */
export const hasDirectNavigationFlag = (): boolean => {
  return sessionStorage.getItem(STORAGE_KEYS.DIRECT_NAVIGATION_FLAG) === 'true';
};

/**
 * Navigate to the purchase result page safely using direct navigation
 * This bypasses React Router to avoid state management issues
 */
export const navigateToPurchaseResult = (data?: TransactionData): void => {
  if (data) {
    storeTransactionData(data);
  }
  
  // No need for flags - force a clean navigation that triggers a fresh page load
  window.location.href = '/purchase-result';
};

/**
 * Extract session ID from URL (hash fragment or query parameter)
 * Consolidated function to handle all Stripe session ID extraction scenarios
 */
export const extractSessionId = (): string | null => {
  // First check for direct hash pattern (most common from Stripe)
  const hash = window.location.hash;
  if (hash) {
    // Look for a Stripe session ID pattern in the hash (cs_test_... or cs_live_...)
    const sessionIdMatch = hash.match(/cs_(?:test|live)_[a-zA-Z0-9_]+/);
    if (sessionIdMatch) {
      return sessionIdMatch[0];
    }
    
    // If the hash is simply the session ID (starts with cs_)
    if (hash.startsWith('#cs_')) {
      return hash.substring(1);
    }
    
    // If the hash contains session_id= parameter
    if (hash.includes('session_id=')) {
      const sessionId = hash.substring(hash.indexOf('session_id=') + 11);
      // If there are other parameters, only take until the next &
      const endIndex = sessionId.indexOf('&');
      return endIndex > -1 ? sessionId.substring(0, endIndex) : sessionId;
    }
  }
  
  // Check URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const querySessionId = urlParams.get('session_id');
  if (querySessionId) {
    return querySessionId;
  }
  
  return null;
};

/**
 * Extract session ID from URL (hash fragment or query parameter)
 * @deprecated Use extractSessionId instead
 */
export const hasSessionIdInUrl = (): string | null => {
  return extractSessionId();
};

/**
 * Clean session ID from URL without refreshing
 */
export const cleanSessionIdFromUrl = (): void => {
  if (window.history && window.history.replaceState) {
    window.history.replaceState({}, document.title, '/purchase-result');
  }
};

/**
 * Get transaction storage key for a specific session
 */
export const getTransactionKey = (sessionId: string): string => {
  return `${STORAGE_KEYS.TRANSACTION_PREFIX}${sessionId}`;
};

// Hook for payment utilities
export const usePaymentUtils = () => {
  // Check if a session ID is in the URL
  const hasSessionId = useCallback(() => {
    return hasSessionIdInUrl();
  }, []);
  
  // Clean session ID from URL
  const cleanSessionId = useCallback(() => {
    try {
      console.log('Cleaning session ID from URL');
      // Only clean the session ID if we're on the purchase-result page
      // to avoid issues with redirects
      const isOnPurchaseResult = window.location.pathname.includes('purchase-result');
      if (isOnPurchaseResult) {
        console.log('On purchase-result page, cleaning session ID');
        cleanSessionIdFromUrl();
      } else {
        console.log('Not on purchase-result page, preserving session ID');
      }
    } catch (error) {
      console.error('Error cleaning session ID:', error);
    }
  }, []);
  
  // Get transaction data from storage
  const getTransactionData = useCallback(() => {
    try {
      return retrieveTransactionData();
    } catch (error) {
      console.error('Error getting transaction data in hook:', error);
      return null;
    }
  }, []);
  
  return {
    hasSessionId,
    cleanSessionId,
    getTransactionData
  };
}; 